import { SyntheticEvent, useEffect, useState } from 'react';
import { Album } from '../../firestore/firestore-types';
import { getCollaboratorProfilePictures, getAlbumOwnerInfo } from '../../firestore/db';
import { DEFAULT_ALBUM_COVER_URL, DEFAULT_AVATAR_URL, resolveImageUrl } from '../../utils/image';

interface AlbumCardProps {
    album: Album;
    handleDeleteAlbum: (album: Album) => void;
}
const AlbumCard = (props: AlbumCardProps) => {
    const [albumOwnerInfo, setAlbumOwnerInfo] = useState<string[]>([]);
    const [collaboratorProfilePictures, setCollaboratorProfilePictures] = useState<string[]>([]);

    useEffect(() => {
        let isMounted = true;

        const loadCardData = async () => {
            const ownerInfo = await getAlbumOwnerInfo(props.album);
            const profilePictures = await getCollaboratorProfilePictures(props.album);

            if (!isMounted || !profilePictures) {
                return;
            }

            if (ownerInfo !== null) {
                setAlbumOwnerInfo(ownerInfo);
            } else {
                console.error("error getting album owner info");
            }

            const ownerPhoto = ownerInfo?.[1];
            const images = ownerPhoto ? [...profilePictures, ownerPhoto] : profilePictures;
            setCollaboratorProfilePictures(images);
        }

        loadCardData();

        return () => {
            isMounted = false;
        }
    }, [props.album])

    const isPublicString = props.album.isPublic ? "Public" : "Private";
    const albumCoverUrl = resolveImageUrl(props.album.photoUrl, DEFAULT_ALBUM_COVER_URL);

    const handleAvatarError = (event: SyntheticEvent<HTMLImageElement>) => {
        event.currentTarget.src = DEFAULT_AVATAR_URL;
    }

    const handleCoverError = (event: SyntheticEvent<HTMLImageElement>) => {
        event.currentTarget.src = DEFAULT_ALBUM_COVER_URL;
    }

    return (
        <div className="album-card">
            <div className="album-card-cover-photo">
                <img
                    className="album-cover-image"
                    src={albumCoverUrl}
                    alt="album cover"
                    onError={handleCoverError}
                />
            </div>
            <a className="album-card-link" href={`/${props.album.owner}/${props.album.albumId}`}>
                <div className="album-card-info">
                    <div className="album-card-title">
                        <h1>{props.album.name}</h1>
                        <p>Album Owner: {albumOwnerInfo[0]}</p>
                    </div>
                    <div className="album-card-description">
                        <p>{props.album.description}</p>
                    </div>
                    <div className="album-card-collaborators">
                        <p>Collaborators:</p>
                        <div className="album-card-collaborator-images">
                            {collaboratorProfilePictures && collaboratorProfilePictures.map((profilePicture, key) => {
                                return (
                                    <img
                                        key={`${profilePicture}-${key}`}
                                        className="album-collaborator-image"
                                        src={resolveImageUrl(profilePicture, DEFAULT_AVATAR_URL)}
                                        alt={`collaborator-pfp-${key}`}
                                        referrerPolicy="no-referrer"
                                        onError={handleAvatarError}
                                    />
                                )
                            })}
                        </div>
                    </div>
                    <div className="album-card-shared-with">
                        <p>Shared With:</p>
                        <div className="album-card-shared-with-values">
                            {props.album.sharedWith.map((sharedWith, index) => {
                                return (
                                    <span className="shared-with-pill" key={`${sharedWith}-${index}`}>{sharedWith}</span>
                                )
                            })}
                        </div>
                    </div>
                    <div className="album-card-public-private">
                        <p>{isPublicString}</p>
                    </div>
                </div>
            </a>
            <button className="deletealbumbutton" onClick={() => props.handleDeleteAlbum(props.album)}>Delete Album</button>
        </div>
    )
}

export default AlbumCard;
